import {
    DialogContent,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import React, {Fragment, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import API from '../../../../app/api';
import _ from 'lodash';
import clsx from 'clsx';
import {useStylesUrinalysis} from './urinalysis';
import {TextFieldDebounce} from '../../../../controls/agyle/textFieldDebounce';
import Switch from '@mui/material/Switch';
import Paper from '@mui/material/Paper';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/Remove';
import IconButton from '@mui/material/IconButton';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import Box from '@mui/material/Box';
import {useDispatch, useSelector} from 'react-redux';
import {urinalysisStripGroups} from '../../../../store/reducers/dicts';
import MenuItem from '@mui/material/MenuItem';
import MessageBox from '../../../../util/messageBox';
import {setSnackbar} from '../../../../store/reducers/globals';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import {getTableScrollbar} from '../../../../util/scrollbar';

const UrinalysisSettings = (props) => {
    const {t} = useTranslation();
    const classes = useStylesUrinalysis();
    const controller = new AbortController();
    const dispatch = useDispatch();

    // States
    const [list, setList] = useState([]);
    const [selectedTest, setSelectedTest] = useState(null);
    const [newItem, setNewItem] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [groups, setGroups] = useState([]);

    useEffect(() => {
        loadTest();

        return () => {
            controller.abort();
        };
    }, []);

    const loadTest = () => {
        API.getAllUrinalysis(controller.signal).then(data => {
            if (!_.isEmpty(data)) {
                setList(_.orderBy(data, ['active', 'description'], ['desc', 'asc']));
            }
        });
    };

    const saveTest = (obj) => {
        API.setUrinalysis(obj, controller.signal)
            .then(data => {
                if (data.status === 200 && data.data) {
                    setSelectedTest(data.data);
                    loadTest();
                    dispatch(setSnackbar({type: 'success', message: 'Data save successful.'}));
                }
            })
            .catch(error => {
                dispatch(setSnackbar({type: 'error', message: `Error on data save. ${error.message}`}));
            });
    };

    const handleActiveStatus = () => {
        if (selectedTest) {
            let clone = _.cloneDeep(selectedTest);
            const status = !clone.active;
            clone.active = status;
            clone.default = !status ? false : clone.default;
            delete clone.__version;
            delete clone.__type;
            delete clone.__href;

            saveTest(clone);
        }
    };

    const editTest = () => {
        setIsEdit(true);
        setNewItem(selectedTest);
        setSelectedTest(null);
    };

    const setDefault = () => {
        let findDefault = _.find(list, {default: true});
        if (findDefault) {
            findDefault.default = false;
            delete findDefault.__version;
            delete findDefault.__type;
            delete findDefault.__href;
            saveTest(findDefault);
        }
        let clone = _.cloneDeep(selectedTest);
        clone.default = true;
        delete clone.__version;
        delete clone.__type;
        delete clone.__href;
        saveTest(clone);
    };

    const isValid = () => {
        let check = !_.isEmpty(groups);
        _.forEach(groups, group => {
            if (!check) return;
            check = !_.isEmpty(group.values);
        });

        return check && newItem?.description && newItem?.description !== '';
    };

    const clear = () => {
        setNewItem(null);
    };

    const cancelEdit = () => {
        setIsEdit(false);
        clear();
    };

    const saveData = () => {
        if (isValid()) {
            let obj = {
                description: newItem.description,
                active: _.isNil(newItem?.active) ? true : newItem?.active,
                tests: groups,
            };
            if (newItem?.__id) {
                Object.assign(obj, {__id: newItem?.__id});
            }

            API.setUrinalysis(obj, controller.signal).then(data => {
                if (data && data.status === 200 && data.data) {
                    dispatch(setSnackbar({type: 'success', message: 'Data save successful.'}));
                    loadTest();
                    clear();
                }
            }).catch(error => {
                dispatch(setSnackbar({type: 'error', message: `Saving data has failed. ${error.message}`}));
            });
        } else {
            dispatch(setSnackbar({
                type: 'warning',
                message: 'The strip test must have a name, not empty groups with at least one item.',
            }));
        }
    };

    return (
        <AgyleDialog
            open={props.open}
            width="xl"
            fullWidth
            noPadding
            disableSave
            closeButtonOnHeader
            title={t(`Total number of urinalysis strip tests: ${list.length}`)}
            onClose={props.onClose}
            buttons={[
                isEdit ?
                    <AgyleButton
                        variant="contained"
                        onClick={cancelEdit}
                        key="cancel-edit"
                    >
                        {t('Cancel Edit')}
                    </AgyleButton> : null,
                <AgyleButton
                    variant="contained"
                    onClick={saveData}
                    key="save-edit"
                >
                    {t(isEdit ? 'Save changes' : 'Add new test')}
                </AgyleButton>,
            ]}
            titleStyle={classes.backgroundOverride}
            actionStyle={classes.backgroundOverride}
            contentStyle={clsx(classes.backgroundOverride, classes.actionNoDivider)}
        >
            <div style={{padding: 15}}>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12}>
                        <AgyleDictionaryField
                            label={t('Tests')}
                            options={list}
                            value={selectedTest}
                            onChange={(event, newValue) => setSelectedTest(newValue)}
                            getOptionLabel={option => option.description}
                            isOptionEqualToValue={(option, value) => option.__id === value.__id}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}
                                     style={{textDecoration: option.active ? '-' : 'line-through'}}>
                                    {option.description}
                                </Box>
                            )}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12} sm={5}>
                        <AgyleButton
                            style={{width: '100%'}}
                            disabled={!selectedTest}
                            variant="contained"
                            onClick={handleActiveStatus}
                        >
                            {!selectedTest && t('Select strip test')}
                            {selectedTest && t(selectedTest.active ? 'Deactivate' : 'Activate')}
                        </AgyleButton>
                    </Grid>

                    <Grid item xs={12} sm={5}>
                        <AgyleButton
                            style={{width: '100%'}}
                            disabled={!selectedTest || !selectedTest.active || selectedTest.default}
                            variant="contained"
                            onClick={setDefault}
                        >
                            {t('Set as default')}
                        </AgyleButton>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                        <AgyleButton
                            style={{width: '100%'}}
                            disabled={!selectedTest}
                            variant="contained"
                            onClick={editTest}
                        >
                            {t('Edit')}
                        </AgyleButton>
                    </Grid>

                    <Grid item xs={12} sm={newItem?.__id ? 11 : 12}>
                        <TextFieldDebounce
                            changeValueTrigger
                            className={classes.fullWidth}
                            label={t('Test name')}
                            agyleRefresh
                            fullWidth
                            value={newItem?.description || ''}
                            onChange={event => setNewItem(prevState => ({
                                ...prevState,
                                description: event.target.value,
                            }))}
                            color="tertiary"
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    {newItem?.__id &&
                        <Grid item xs={12} sm={1}>
                            <FormControlLabel
                                value={newItem?.active}
                                control={<Switch color="primary" size="small"/>}
                                label={t('Active')}
                                labelPlacement="top"
                                onChange={() => setNewItem(prevState => ({
                                    ...prevState,
                                    active: !prevState?.active,
                                }))}
                            />
                        </Grid>
                    }

                    <Grid item xs={12}>
                        <Grid container direction="row" spacing={1}>
                            <Grid item xs={12}>
                                <GroupAdder
                                    data={newItem}
                                    onChange={setGroups}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </div>
        </AgyleDialog>

    );
};

export default UrinalysisSettings;

const GroupAdder = (props) => {
    const {t} = useTranslation();
    const classes = useStylesUrinalysis();
    const dispatch = useDispatch();
    const dictUrinalysis = useSelector(urinalysisStripGroups);

    // Main list
    const [list, setList] = useState([]);

    // Selector state
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);

    // State
    const [newItem, setNewItem] = useState(null);
    const typeNewItem = useRef(null);
    const isEdit = useRef(false);

    const [openAdd, setOpenAdd] = useState(false);

    //Confirm
    const [openConfirm, setOpenConfirm] = useState(false);
    const deleteItem = useRef(null);

    useEffect(() => {
        setList(props.data && props.data?.tests ? props.data?.tests : []);
        setSelectedGroup(null);
        setSelectedItem(null);
    }, [props.data]);

    useEffect(() => {
        const clone = [];

        _.forEach(list, (group, index) => {
            const values = [];
            _.forEach(group.values, (item, indexItem) => {
                values.push({...item, position: indexItem + 1});
            });

            clone.push({...group, values: values, position: index + 1});
        });
        props.onChange(clone);
    }, [list]);

    const handleChangeNewGroup = (event) => {
        const {value} = event.target;
        setNewItem(prevState => ({
            ...prevState,
            shortcut: value.shortcut,
            name: value.__description,
            unit: value.unit,
        }));
    };

    const handleChangeColour = (event) => {
        const {value} = event.target;
        setNewItem(prevState => ({
            ...prevState,
            colour: value.hex,
            grainType: value.grainType,
        }));
    };

    const renderTableElements = (element, type) => {
        // eslint-disable-next-line default-case
        switch (element) {
            case 'groups': {
                if (type === 'columns') {
                    return (
                        <TableRow>
                            <TableCell className={classes.headCell}>
                                {t('Name')}
                            </TableCell>

                            <TableCell align="center" className={classes.headCell}>
                                {t('Shortcut')}
                            </TableCell>

                            <TableCell align="center" className={classes.headCell}>
                                {t('Unit')}
                            </TableCell>

                            <TableCell align="right" className={classes.headCell}>
                                {t('Move')}
                            </TableCell>
                        </TableRow>
                    );
                } else {
                    return (
                        list.map((row, index) => (
                            <TableRow
                                key={row.name + index}
                                sx={{'&:last-child td, &:last-child th': {border: 0}}}
                                className={classes.tableRow}
                                onClick={() => {
                                    setSelectedItem(null);
                                    setSelectedGroup(row);
                                }}
                            >
                                <TableCell
                                    className={clsx(classes.rowCell, index % 2 && 'odd')}
                                >
                                    {row.name}
                                </TableCell>

                                <TableCell
                                    align="center"
                                    className={clsx(classes.rowCell, index % 2 && 'odd')}
                                >
                                    {row.shortcut}
                                </TableCell>

                                <TableCell
                                    align="center"
                                    className={clsx(classes.rowCell, index % 2 && 'odd')}
                                >
                                    {row.unit}
                                </TableCell>

                                <TableCell
                                    align="right"
                                    className={clsx(classes.rowCell, index % 2 && 'odd')}
                                >
                                    <Grid container direction={'row'} justifyContent="flex-end">
                                        <IconButton
                                            disableRipple
                                            size="small"
                                            onClick={moveGroup.bind(this, row, 'up')}
                                        >
                                            <ArrowDropUpIcon/>
                                        </IconButton>

                                        <IconButton
                                            disableRipple
                                            size="small"
                                            onClick={moveGroup.bind(this, row, 'down')}
                                        >
                                            <ArrowDropDownIcon/>
                                        </IconButton>
                                    </Grid>
                                </TableCell>
                            </TableRow>
                        ))
                    );
                }
            }

            case 'items': {
                if (type === 'columns') {
                    return (
                        <TableRow>
                            <TableCell className={classes.headCell}>
                                {t('Value')}
                            </TableCell>

                            <TableCell align="left" className={classes.headCell}>
                                {t('Colour')}
                            </TableCell>

                            <TableCell align="right" className={classes.headCell}>
                                {t('Move')}
                            </TableCell>
                        </TableRow>
                    );
                } else {
                    return (
                        selectedGroup?.values?.map((row, index) => (
                            <TableRow
                                key={index + row.value + index}
                                sx={{'&:last-child td, &:last-child th': {border: 0}}}
                                className={classes.tableRow}
                                onClick={() => setSelectedItem(row)}
                            >
                                <TableCell
                                    className={clsx(classes.rowCell, index % 2 && 'odd')}
                                >
                                    {row.value}
                                </TableCell>

                                <TableCell
                                    align="left"
                                    className={clsx(classes.rowCell, index % 2 && 'odd')}
                                >
                                    <div
                                        className={classes.testBar}
                                        style={{
                                            backgroundColor: row.colour,
                                            backgroundImage: row.grainType ? `url(/image/urinalysis/grain_typ_${row.grainType}.png)` : '',
                                            backgroundSize: 'contain',
                                        }}
                                    />
                                </TableCell>

                                <TableCell
                                    align="right"
                                    className={clsx(classes.rowCell, index % 2 && 'odd')}
                                >
                                    <Grid container direction={'row'} justifyContent="flex-end">
                                        <IconButton
                                            disableRipple
                                            size="small"
                                            onClick={moveItem.bind(this, row, 'up')}
                                        >
                                            <ArrowDropUpIcon/>
                                        </IconButton>

                                        <IconButton
                                            disableRipple
                                            size="small"
                                            onClick={moveItem.bind(this, row, 'down')}
                                        >
                                            <ArrowDropDownIcon/>
                                        </IconButton>
                                    </Grid>
                                </TableCell>
                            </TableRow>
                        ))
                    );
                }
            }
        }
    };

    const findColors = (value) => {
        const colorArray = _.find(dictUrinalysis, {
            __description: selectedGroup?.name,
            shortcut: selectedGroup?.shortcut,
        });

        return _.sortBy(colorArray?.colours, ['hex']).map(element => (
            <MenuItem key={element.grainType ? `${element.hex}_${element.grainType}` : element.hex}
                      value={element}
            >
                <div style={{
                    width: '100%',
                    height: 50,
                    backgroundColor: element.hex,
                    backgroundImage: element.grainType ? `url(/image/urinalysis/grain_typ_${element.grainType}.png)` : '',
                    backgroundSize: 'contain',
                }}/>
            </MenuItem>
        ));
    };

    const renderSelectValue = () => {
        return function (p1) {
            return <div
                style={{
                    height: 25,
                    width: '100%',
                    backgroundColor: newItem?.colour,
                    backgroundImage: newItem.grainType ? `url(/image/urinalysis/grain_typ_${newItem.grainType}.png)` : '',
                    backgroundSize: 'contain',
                }}
            />;
        };
    };

    const renderNewItem = () => {
        // eslint-disable-next-line default-case
        switch (typeNewItem.current) {
            case 'groups': {
                return (
                    <Grid item xs={12}>
                        <FormControl style={{width: '100%'}}>
                            <InputLabel>
                                {t('Groups')}
                            </InputLabel>
                            <Select
                                label={t('Groups')}
                                defaultValue={''}
                                onChange={handleChangeNewGroup}
                            >
                                {dictUrinalysis.map(element => (
                                    <MenuItem key={element.shortcut}
                                              value={element}>
                                        {`${element.__description} [${element.shortcut}]`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                );
            }

            case 'items': {
                return (
                    <>
                        <Grid item xs={12}>
                            <TextFieldDebounce
                                color="tertiary"
                                className={classes.fullWidth}
                                label={t('Item value')}
                                value={newItem?.value || ''}
                                onChange={event => setNewItem(prevState => ({
                                    ...prevState,
                                    value: event.target.value,
                                }))}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl style={{width: '100%'}}>
                                <InputLabel>
                                    {t('Item colour')}
                                </InputLabel>
                                <Select
                                    label={t('Item colour')}
                                    value={newItem?.colour || ''}
                                    onChange={handleChangeColour}
                                    renderValue={renderSelectValue()}
                                >
                                    {findColors()}
                                </Select>
                            </FormControl>
                        </Grid>
                    </>
                );
            }
        }
    };

    const addGroupList = (cloneGroup) => {
        let clone = _.cloneDeep(list);
        const index = _.findIndex(clone, {name: cloneGroup.name, shortcut: cloneGroup.shortcut});
        if (index !== -1) {
            _.remove(clone, {name: cloneGroup.name, shortcut: cloneGroup.shortcut});
            clone.splice(index, 0, cloneGroup);
        }
        setList(clone);
    };

    const moveGroup = (item, direction) => {
        let clone = _.cloneDeep(list);
        const index = _.findIndex(clone, {name: item.name, shortcut: item.shortcut});

        if (index !== -1) {
            if (direction === 'up' && index > 0) {
                _.remove(clone, {name: item.name, shortcut: item.shortcut});
                clone.splice(index - 1, 0, item);
            } else if (direction === 'down' && index < clone.length - 1) {
                _.remove(clone, {name: item.name, shortcut: item.shortcut});
                clone.splice(index + 1, 0, item);
            }
            setList(clone);
        }
    };

    const moveItem = (item, direction) => {
        let clone = _.cloneDeep(selectedGroup);
        const index = _.findIndex(clone.values, {value: item.value});

        if (index !== -1) {
            if (direction === 'up' && index > 0) {
                _.remove(clone?.values, {value: item?.value});
                clone?.values?.splice(index - 1, 0, item);
            } else if (direction === 'down' && index < clone?.values?.length - 1) {
                _.remove(clone?.values, {value: item?.value});
                clone?.values?.splice(index + 1, 0, item);
            }
            setSelectedGroup(clone);
            addGroupList(clone);
        }
    };

    const addItem = (type) => {
        typeNewItem.current = type;
        setOpenAdd(true);
    };

    const editElement = (type) => {
        typeNewItem.current = type;
        isEdit.current = true;
        setNewItem(type === 'groups' ? {...selectedGroup} : {...selectedItem});
        setOpenAdd(true);
    };

    const handleClose = () => {
        isEdit.current = false;
        setNewItem(null);
        setOpenAdd(false);
    };

    const valid = (type) => {
        if (type === 'groups') {
            const check = !_.isNull(newItem) && (newItem?.name && newItem?.name !== '') && (newItem?.shortcut && newItem?.shortcut !== '');
            if (!check) {
                dispatch(setSnackbar({type: 'warning', message: 'Name and shortcut cannot be empty.'}));
            }

            return check;
        } else {
            const check = !_.isNull(newItem) && (newItem?.value && newItem?.value !== '') && (newItem?.colour && newItem?.colour !== '' && newItem?.colour !== '#NaNNaNNaN');
            if (!check) {
                dispatch(setSnackbar({type: 'warning', message: 'Value and Colour cannot be empty.'}));
            }
            return check;
        }
    };

    const handleAccept = () => {
        const template = {
            name: '',
            position: 0,
            shortcut: '',
            values: [],
        };

        if (valid('groups')) {
            let clone = _.cloneDeep(list);
            const index = _.findIndex(clone, {name: newItem.name, shortcut: newItem.shortcut});

            if (isEdit.current && index !== -1) {
                setOpenAdd(false);
                _.remove(clone, {name: newItem.name, shortcut: newItem.shortcut});
                clone.splice(index, 0, {...template, ...newItem});
                setList(clone);

                isEdit.current = false;
                setNewItem(null);
            } else {
                if (index !== -1) {
                    dispatch(setSnackbar({type: 'warning', message: 'Group already exists.'}));
                } else {
                    setOpenAdd(false);
                    setList(prevState => [
                        ...prevState,
                        {...template, ...newItem},
                    ]);

                    isEdit.current = false;
                    setNewItem(null);
                }
            }
        }
    };

    const handleAcceptItem = () => {
        const template = {
            colour: '',
            grainType: null,
            position: 0,
            value: '1',
        };

        if (valid('items')) {
            const cloneGroup = _.cloneDeep(selectedGroup);

            if (isEdit.current) {
                const index = _.findIndex(cloneGroup?.values, {value: selectedItem.value});
                setOpenAdd(false);
                _.remove(cloneGroup?.values, {value: selectedItem.value});
                cloneGroup?.values?.splice(index, 0, {...template, ...newItem});

                setSelectedGroup(cloneGroup);
                addGroupList(cloneGroup);

                isEdit.current = false;
                setNewItem(null);
            } else {
                const index = _.findIndex(cloneGroup?.values, {value: newItem.value});
                if (index !== -1) {
                    dispatch(setSnackbar({type: 'warning', message: 'Value already exists.'}));
                } else {
                    setOpenAdd(false);
                    cloneGroup?.values.push({...template, ...newItem});

                    setSelectedGroup(cloneGroup);
                    addGroupList(cloneGroup);

                    isEdit.current = false;
                    setNewItem(null);
                }
            }
        }
    };

    const handleRemove = () => {
        if (deleteItem.current) {
            if (deleteItem.current === 'groups') {
                const clone = _.cloneDeep(list);
                _.remove(clone, {name: selectedGroup.name, shortcut: selectedGroup.shortcut});
                setList(clone);
                setSelectedGroup(null);
            } else {
                const clone = _.cloneDeep(selectedGroup);
                _.remove(clone?.values, {value: selectedItem.value});
                setSelectedGroup(clone);
                addGroupList(clone);
            }
        }

        deleteItem.current = null;
        setOpenConfirm(false);
    };

    const renderSelector = (type) => {
        return (
            <div className={clsx(classes.adderBorder, 'scroll-normal-white')}>
                <div className={classes.title}>
                    {t(_.capitalize(type || '-'))}
                </div>

                <Divider style={{width: '100%', marginBottom: 5}}/>

                <div className={classes.adder}>
                    <TableContainer component={Paper} className={getTableScrollbar()}>
                        <Table size="small" aria-label="a dense table">
                            <TableHead>
                                {renderTableElements(type, 'columns')}
                            </TableHead>
                            <TableBody>
                                {renderTableElements(type, 'rows')}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>

                <Grid container direction="row" spacing={1} sx={{mt: 1}}>
                    <Grid item xs={12} sm={type !== 'groups' ? 4 : 6}>
                        <AgyleButton
                            size="small"
                            style={{width: '100%'}}
                            variant="contained"
                            startIcon={<AddIcon/>}
                            onClick={addItem.bind(this, type)}
                            disabled={type === 'groups' ? false : !selectedGroup}
                        >
                            {t('Add')}
                        </AgyleButton>
                    </Grid>

                    {type !== 'groups' &&
                        <Grid item xs={12} sm={4}>
                            <AgyleButton
                                size="small"
                                style={{width: '100%'}}
                                variant="contained"
                                startIcon={<EditIcon/>}
                                disabled={type === 'groups' ? !selectedGroup : !selectedItem}
                                onClick={editElement.bind(this, type)}
                            >
                                {t('Edit')}
                            </AgyleButton>
                        </Grid>
                    }

                    <Grid item xs={12} sm={type !== 'groups' ? 4 : 6}>
                        <AgyleButton
                            size="small"
                            style={{width: '100%'}}
                            variant="contained"
                            startIcon={<RemoveIcon/>}
                            disabled={type === 'groups' ? !selectedGroup : !selectedItem}
                            onClick={() => {
                                deleteItem.current = type;
                                setOpenConfirm(true);
                            }}
                        >
                            {t('Remove')}
                        </AgyleButton>
                    </Grid>
                </Grid>
            </div>
        );
    };

    return (
        <Fragment>
            <Grid container direction="row" spacing={2}>
                {/* Groups */}
                <Grid item xs={12} sm={6}>
                    {renderSelector('groups')}
                </Grid>

                {/* Values in groups */}
                <Grid item xs={12} sm={6}>
                    {renderSelector('items')}
                </Grid>

                {openAdd &&
                    <AgyleDialog
                        onClose={handleClose}
                        open={openAdd}
                        disableEscapeKeyDown
                        closeButtonOnHeader
                        title={t('Add new element')}
                        fullWidth
                        noPadding
                        onSave={typeNewItem.current === 'groups' ? handleAccept : handleAcceptItem}
                        hideBackdrop
                    >
                        <DialogContent>
                            <Grid container spacing={2} style={{minWidth: 400}}>
                                {renderNewItem()}
                            </Grid>
                        </DialogContent>
                    </AgyleDialog>
                }
            </Grid>

            <MessageBox
                onClose={() => setOpenConfirm(false)}
                title={t('Confirmation')}
                message={t('Are you sure you want to delete this item?')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={handleRemove}
                open={openConfirm}
            />
        </Fragment>
    );
};